import type { MessageKey } from './fr.ts';

export const en: Record<MessageKey, string> = {
  'app.name': 'La Gazette',
  'app.tagline': 'The scandal sheet where you make the headlines.',
  'lang.switch': 'Français',
  'lang.label': 'Change language',
  'sound.mute': 'Mute sound',
  'sound.unmute': 'Turn sound on',
  'connection.lost': 'Connection lost. Reconnecting…',

  'home.name.label': 'Your name',
  'home.name.placeholder': 'First name or nickname',
  'home.create': 'Open an investigation',
  'home.or': 'or join a case',
  'home.code.label': 'Case code',
  'home.join': 'Join',
  'home.name.required': 'Write your name to open the case.',
  'home.code.required': 'Case codes are four letters long.',
  'home.joinInvite': 'Join case {code}',
  'home.createInstead': 'Open a new investigation instead',
  'home.rules.title': 'How the investigation works',
  'home.rules.1': 'Confessions: everyone secretly answers a few questions about themselves.',
  'home.rules.2': 'Writing: you read two suspects’ confessions, write one true rumor from them, then a false one about anyone you like.',
  'home.rules.3': 'Publication: the Gazette comes out on every screen.',
  'home.rules.4': 'Investigation: for each rumor, true or false, and who wrote it? The victim doesn’t vote and may lie.',
  'home.rules.5': 'Reveal: the stamp comes down and the pen is unmasked. You score by bluffing and deducing.',
  'home.rules.players': '4 to 10 players, each on their own phone. About 20 minutes.',
  'home.sample.headline': 'Thomas reportedly cried at Ratatouille in the cinema, back in 2023',
  'home.sample.subtitle': 'Our sources say he blamed a popcorn allergy.',

  'error.ROOM_NOT_FOUND': 'No case has that code.',
  'error.ROOM_FULL': 'This case is full.',
  'error.GAME_IN_PROGRESS': 'The investigation has already started without you.',
  'error.NAME_INVALID': 'We need a name, 20 characters at most.',
  'error.NAME_TAKEN': 'Someone in this case already has that name.',
  'error.SESSION_INVALID': 'Your seat in this case has expired.',
  'error.NOT_HOST': 'Only the host can do that.',
  'error.WRONG_PHASE': 'Too late: the game has moved on.',
  'error.NOT_ENOUGH_PLAYERS': 'You need at least 4 suspects to start.',
  'error.ADULTS_REQUIRED': 'Confirm everyone is an adult for this intensity.',
  'error.BOTS_DISABLED': 'Test suspects are disabled on this server.',
  'error.INVALID_INPUT': 'That input wasn’t accepted.',
  'error.RATE_LIMITED': 'Easy! Try again in a moment.',
  'error.NETWORK': 'Can’t reach the server. Check your connection and try again.',
  'error.KICKED': 'The host removed you from the case.',

  'lobby.case': 'Case',
  'lobby.share': 'Share link',
  'lobby.copied': 'Link copied',
  'lobby.qr.show': 'Show QR code',
  'lobby.qr.hide': 'Hide QR code',
  'lobby.qr.alt': 'QR code to join case {code}',
  'lobby.lanHint': 'This page is on localhost: for phones, start the server with PUBLIC_URL set to this machine’s IP address.',
  'lobby.suspects': 'Suspects',
  'lobby.you': 'you',
  'lobby.host': 'host',
  'lobby.bot': 'test',
  'lobby.offline': 'offline',
  'lobby.kick': 'Remove {name}',
  'lobby.reroll': 'New portrait',
  'lobby.addBot': 'Add a test suspect',
  'lobby.need.one': '{n} more suspect needed to start.',
  'lobby.need.other': '{n} more suspects needed to start.',
  'lobby.start': 'Start the investigation',
  'lobby.waitHost': '{name} will start the investigation.',

  'settings.title': 'Settings',
  'settings.readonly': 'Settings chosen by the host',
  'settings.intensity': 'Intensity',
  'settings.intensity.soft': 'Soft',
  'settings.intensity.spicy': 'Spicy',
  'settings.intensity.unfiltered': 'Unfiltered',
  'settings.intensity.help.soft': 'Light questions, fine with colleagues.',
  'settings.intensity.help.spicy': 'More personal. Adults only.',
  'settings.intensity.help.unfiltered': 'Nothing is off limits. Adults only.',
  'settings.adults': 'Every player is 18 or older',
  'settings.questions': 'Questions per player',
  'settings.pace': 'Pace',
  'settings.pace.relaxed': 'Relaxed',
  'settings.pace.normal': 'Normal',
  'settings.pace.fast': 'Fast',
  'settings.corbeau': '“The Raven” variant',
  'settings.corbeau.help': 'One secret player writes only false rumors. If never unmasked, they win 5 points.',
  'settings.typography': 'Uniform typography',
  'settings.typography.help': 'Punctuation, quotes and capitals are harmonized, so nobody is betrayed by how they type.',
  'settings.on': 'on',
  'settings.off': 'off',

  'phase.confessions': 'Confessions',
  'phase.writing': 'Writing',
  'phase.publication': 'Publication',
  'phase.investigation': 'Investigation',
  'phase.reveal': 'Reveal',
  'phase.plume': 'Golden Quill',
  'phase.final': 'Results',
  'timer.label': 'Time left',

  'conf.title': 'Your statement',
  'conf.intro': 'Answer in secret. Two other suspects will read your answers to write a true rumor, and nobody will know which two.',
  'conf.placeholder': 'Your answer…',
  'conf.sign': 'Sign my statement',
  'conf.signed': 'Statement signed',
  'conf.edit': 'Edit my statement',
  'conf.needOne': 'Answer at least one question before signing.',
  'progress.signed': '{done} of {total} have signed',

  'write.intro': 'You received the statements of {names}. Nobody knows you read them.',
  'write.noSources': 'Nobody confessed, so you’ll write two false rumors.',
  'write.corbeau.title': 'You are the Raven',
  'write.corbeau.body': 'Write two false rumors. If nobody ever unmasks you, you win 5 points.',
  'write.sources': 'Evidence received',
  'write.use': 'Use this evidence',
  'write.used': 'Evidence selected',
  'write.true.title': 'True rumor',
  'write.true.help': 'Pick a piece of evidence above, then rewrite it tabloid-style.',
  'write.false.title': 'False rumor',
  'write.false.help': 'Make it all up, about anyone you like.',
  'write.victim': 'Victim',
  'write.kicker': 'Section',
  'write.headline': 'Headline',
  'write.headline.placeholder': 'Spotted…',
  'write.subtitle': 'Standfirst, optional',
  'write.subtitle.placeholder': 'Our sources say…',
  'write.templates': 'Fill in the blanks',
  'write.invent': 'Help me make it up',
  'write.send': 'Send to the newsroom',
  'write.sent': 'Rumors sent to the newsroom',
  'write.edit': 'Edit my rumors',
  'write.missing.source': 'Pick a piece of evidence.',
  'write.missing.victim': 'Pick a victim.',
  'write.missing.headline': 'The headline needs at least 8 characters.',
  'write.chars': '{n} / {max}',
  'progress.sent': '{done} of {total} have sent theirs',

  'kicker.exclusive': 'Exclusive',
  'kicker.scandal': 'Scandal',
  'kicker.revelations': 'Revealed',
  'kicker.spotted': 'Spotted',
  'kicker.indiscretion': 'Indiscretion',
  'kicker.breaking': 'Breaking',

  'pub.title': 'The Gazette is out',
  'pub.count.one': '{n} rumor in print',
  'pub.count.other': '{n} rumors in print',
  'pub.read': 'Read the gazette',
  'pub.close': 'Fold the gazette',
  'pub.start': 'Open the investigation',
  'pub.waitHost': 'The investigation opens in a moment.',

  'inv.case': 'Case {n} of {total}',
  'inv.concerns': 'About {name}',
  'inv.truth': 'True or false?',
  'inv.true': 'True',
  'inv.or': 'or',
  'inv.false': 'False',
  'inv.author': 'Who wrote it?',
  'inv.accuse': 'Accuse {name}',
  'inv.victim.title': 'It’s about you.',
  'inv.victim.body': 'Only you know the truth, and you may lie: every investigator you fool earns you 1 point.',
  'inv.victim.react': 'React publicly',
  'inv.author.note': 'This one is yours. Vote anyway to cover your tracks: your ballot doesn’t count.',
  'inv.reaction': 'says {name}',
  'progress.voted': '{done} of {total} ballots',

  'reaction.deny': 'That’s false!',
  'reaction.nocomment': 'No comment.',
  'reaction.confess': 'Okay… it’s true.',
  'reaction.lawyer': 'Calling my lawyer.',
  'reaction.nonsense': 'Nonsense!',
  'reaction.never': 'You’ll never know.',

  'rev.true': 'True',
  'rev.false': 'False',
  'rev.author.stamp': 'Author',
  'rev.fooled.none': 'Nobody was fooled.',
  'rev.fooled.one': '{n} investigator fooled',
  'rev.fooled.other': '{n} investigators fooled',
  'rev.found': 'Pen identified by {names}',
  'rev.hidden': 'Nobody identified the pen.',
  'rev.points': 'Points from this case',
  'rev.noPoints': 'No points on this case.',
  'rev.next': 'Next case',
  'rev.toPlume': 'On to the Golden Quill',
  'rev.toResults': 'See the results',
  'reason.truth': 'right call',
  'reason.author-found': 'pen identified',
  'reason.fooled': 'investigators fooled',
  'reason.undetected': 'never unmasked',
  'reason.victim-bluff': 'victim’s bluff',
  'reason.corbeau': 'Raven never unmasked',

  'plume.title': 'The Golden Quill',
  'plume.body': 'Vote for the best headline of the night. All glory, no points.',
  'plume.own': 'Your article',
  'plume.vote': 'Give the quill to this article',
  'plume.voted': 'Quill awarded',
  'progress.plume': '{done} of {total} have voted',

  'final.title': 'Results',
  'final.points': '{n} pts',
  'final.rank': '#{n}',
  'final.rank.first': '#1',
  'final.empty': 'No rumor was published. The newsroom took the night off.',
  'final.corbeau': 'The Raven was {name}.',
  'final.download': 'Download the gazette',
  'final.share': 'Share the gazette',
  'final.shareText': 'Our gazette from tonight',
  'final.restart': 'New investigation',
  'final.leave': 'Leave the case',
  'final.leave.confirm': 'Confirm: leave',
  'final.waitHost': '{name} can start a new investigation.',
  'title.plume': 'Golden Quill',
  'title.plume.help': 'Best headline, voted by the group',
  'title.sleuth': 'Sharpest sleuth',
  'title.sleuth.help': 'Identified the most pens',
  'title.bluffer': 'Ace bluffer',
  'title.bluffer.help': 'Lied best as a victim',
  'title.viper': 'Poison pen',
  'title.viper.help': 'Fooled the most as an author',
  'title.corbeau-escaped': 'The Raven got away',
  'title.corbeau-escaped.help': '5 points, never unmasked',
  'title.corbeau-caught': 'The Raven, unmasked',
  'title.corbeau-caught.help': 'Caught red-handed',

  'menu.leave': 'Leave the case',
  'menu.leave.confirm': 'Confirm: leave',

  'gz.motto': 'Journal of high society and low instincts',
  'gz.edition': 'No.\u00A0{n}',
  'gz.price': 'Price: one secret',
  'gz.count.one': '{n} rumor',
  'gz.count.other': '{n} rumors',
  'gz.concerns': 'About {name}',
  'gz.signed': 'Written by {name}',
  'gz.palmares': 'Tonight’s results',
  'gz.footer': 'La Gazette, the game where you make the headlines',
  'gz.ad.1': 'DR. GOSSIP’S TONIC. Cures curiosity in three drops. Side effect: curiosity.',
  'gz.ad.2': 'LOST: one reputation. Generous reward for its return in good condition.',
  'gz.ad.3': 'BLUFFING BY CORRESPONDENCE. First lesson free. The rest too, trust us.',
  'gz.ad.4': 'ANONYMOUS QUILLS delivered in plain wrapping. Discretion guaranteed, mostly.',
  'gz.ad.5': 'WITNESSES SOUGHT for last night’s party. Do not come forward under any circumstances.',
  'fiche.number': 'Card No.\u00A0{n}',
  'fiche.m1': 'Height',
  'fiche.m2': 'Reach',
  'fiche.m3': 'Head, length',
  'fiche.m4': 'Head, width',
  'fiche.m5': 'Right ear',
  'fiche.name': 'Name',
  'fiche.case': 'Case {code}',
};
